import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Icon,
  Metric,
  Table,
  viLabel,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { BarChart, type BarData } from '@/components/charts/BarChart';
import { ExportButton } from '@/components/common/ExportButton';
import { useViewport } from '@/lib/viewport';
import { useScope } from '@/lib/scope';
import { useMockTable } from '@/hooks/useMockTable';
import { useForecastResults } from '@/hooks/useForecast';
import { formatNumber, toISODate } from '@/lib/utils';
import type { ForecastResult } from '@/domain';

const HORIZON_DAYS = 14;

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isMobile, vw } = useViewport();
  const { currentWarehouse } = useScope();
  const navigate = useNavigate();
  const customers = useMockTable('customers');
  const results = useForecastResults({ warehouseId: currentWarehouse?.id });

  const customer = customers.find((c) => c.id === id);
  const today = toISODate(new Date());
  const horizonEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + HORIZON_DAYS - 1);
    return toISODate(d);
  }, [today]);

  const detail = useMemo(
    () => results.filter((r) => r.customer_id === id && r.date >= today && r.date <= horizonEnd),
    [results, id, today, horizonEnd],
  );

  const totalQty = detail.reduce((s, r) => s + r.forecast_qty, 0);
  const totalHours = detail.reduce((s, r) => s + r.total_workload_hours, 0);
  const avgWlu =
    detail.length > 0
      ? detail.reduce((s, r) => s + r.wlu_per_order_minutes, 0) / detail.length
      : 0;
  const peakDay = detail.reduce<ForecastResult | null>(
    (max, r) => (max == null || r.forecast_qty > max.forecast_qty ? r : max),
    null,
  );

  const chartData: BarData[] = detail
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((r) => ({
      label: r.date.slice(8),
      value: r.forecast_qty,
      tone:
        r.event_type === 'MEGA_SALE' || r.event_type === 'DOUBLE_DAY'
          ? 'critical'
          : ['PAYDAY', 'MID_MONTH', 'MINI_SPIKE'].includes(r.event_type)
            ? 'warning'
            : 'default',
      highlight: r.date === today,
    }));

  const cols: Column<ForecastResult>[] = [
    {
      header: 'Ngày',
      mono: true,
      width: 100,
      render: (r) => r.date,
    },
    {
      header: 'Sự kiện',
      render: (r) => (
        <Badge
          kind={
            r.event_type === 'NORMAL'
              ? 'DRAFT'
              : r.event_type === 'MEGA_SALE'
                ? 'CRITICAL'
                : 'WARNING'
          }
          size="sm"
        >
          {viLabel(r.event_type)}
        </Badge>
      ),
    },
    {
      header: 'Đơn dự báo',
      align: 'right',
      mono: true,
      render: (r) => formatNumber(r.forecast_qty),
    },
    {
      header: 'WLU/đơn',
      align: 'right',
      mono: true,
      priority: 2,
      render: (r) => r.wlu_per_order_minutes.toFixed(2) + 'm',
    },
    {
      header: 'Workload',
      align: 'right',
      mono: true,
      priority: 2,
      render: (r) => r.total_workload_hours.toFixed(1) + 'h',
    },
    {
      header: 'Baseline',
      align: 'right',
      mono: true,
      priority: 3,
      render: (r) =>
        formatNumber(Math.round(r.provenance.historical_baseline_avg)) +
        (r.provenance.fallback_used ? '*' : ''),
    },
    {
      header: 'Multipliers',
      mono: true,
      priority: 3,
      render: (r) => (
        <span style={{ fontSize: 11 }}>
          ×{r.provenance.monthly_multiplier.toFixed(2)} · ×
          {r.provenance.event_multiplier.toFixed(2)}
        </span>
      ),
    },
    {
      header: 'Conf',
      render: (r) =>
        r.confidence.is_low_confidence ? (
          <Badge kind="WARNING" size="sm">
            Thấp
          </Badge>
        ) : (
          <Badge kind="APPROVED" size="sm">
            Tốt
          </Badge>
        ),
    },
  ];

  if (!customer) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: 'var(--bx-text-muted)',
            }}
          >
            Không tìm thấy khách hàng <code style={{ fontFamily: 'var(--bx-font-mono)' }}>{id}</code>
            <div style={{ marginTop: 12 }}>
              <Button
                variant="primary"
                onClick={() => navigate('/planner/forecast/customer')}
              >
                Quay lại danh sách
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Lập kế hoạch', 'Dự báo', 'Khách hàng', customer.code]}
        title={customer.name}
        subtitle={`${customer.code} · ${viLabel(customer.tier)} · ${currentWarehouse?.code ?? '—'} · ${HORIZON_DAYS} ngày tới`}
        status={<Badge kind={customer.tier === 'KEY' ? 'APPROVED' : 'INFO'}>{viLabel(customer.tier)}</Badge>}
        actions={
          <>
            <Button
              variant="outline"
              icon="users"
              onClick={() => navigate('/planner/forecast/customer')}
            >
              Danh sách
            </Button>
            <ExportButton
              rows={detail.map((r) => ({
                date: r.date,
                event_type: r.event_type,
                forecast_qty: r.forecast_qty,
                wlu_minutes: r.wlu_per_order_minutes.toFixed(2),
                workload_hours: r.total_workload_hours.toFixed(2),
                baseline_avg: r.provenance.historical_baseline_avg.toFixed(0),
                monthly_mult: r.provenance.monthly_multiplier,
                event_mult: r.provenance.event_multiplier,
                low_confidence: r.confidence.is_low_confidence,
              }))}
              columns={[
                { header: 'Ngày', key: 'date' },
                { header: 'Sự kiện', key: 'event_type' },
                { header: 'Đơn dự báo', key: 'forecast_qty' },
                { header: 'WLU phút/đơn', key: 'wlu_minutes' },
                { header: 'Workload (h)', key: 'workload_hours' },
                { header: 'Baseline avg', key: 'baseline_avg' },
                { header: 'Monthly mul', key: 'monthly_mult' },
                { header: 'Event mul', key: 'event_mult' },
                { header: 'Low conf', key: 'low_confidence' },
              ]}
              filename={`${customer.code}-${today}`}
            />
          </>
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <div
          style={{
            display: 'grid',
            gap: 12,
            marginBottom: 16,
            gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          }}
        >
          <Metric
            label="Tổng đơn 14N"
            value={formatNumber(totalQty)}
            sublabel={`${detail.length} ngày forecast`}
          />
          <Metric
            label="Workload"
            value={`${formatNumber(Math.round(totalHours))}h`}
            sublabel="pack"
          />
          <Metric
            label="Phút/đơn TB"
            value={avgWlu.toFixed(2)}
            sublabel="weighted UPH"
          />
          <Metric
            label="Ngày peak"
            value={peakDay ? formatNumber(peakDay.forecast_qty) : '—'}
            sublabel={peakDay ? `${peakDay.date.slice(5)} · ${viLabel(peakDay.event_type)}` : '—'}
            deltaTone="up"
          />
        </div>

        <Card title="Product mix" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {customer.product_mix.map((m) => (
              <div
                key={m.product_group}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  background: 'var(--bx-surface-2)',
                  borderRadius: 9999,
                  fontSize: 12.5,
                }}
              >
                <Icon name="cube" size={11} style={{ color: 'var(--bx-primary)' }} />
                <strong>{viLabel(m.product_group)}</strong>
                <span style={{ fontFamily: 'var(--bx-font-mono)', color: 'var(--bx-text-muted)' }}>
                  {Math.round(m.share * 100)}%
                </span>
              </div>
            ))}
          </div>
        </Card>

        {detail.length > 0 && (
          <Card title="Xu hướng 14 ngày" style={{ marginBottom: 16 }}>
            <BarChart data={chartData} height={160} />
          </Card>
        )}

        <Card title="Chi tiết theo ngày" subtitle="* baseline được fallback từ event_type khác" padding={0}>
          <Table
            columns={cols}
            rows={detail.slice().sort((a, b) => a.date.localeCompare(b.date))}
            getRowKey={(r) => r.id}
            emptyText="Chưa có dự báo cho khách hàng này"
          />
        </Card>
      </div>
    </div>
  );
}
